import { isValidHtmlTag, isZyTag } from '@/components/search/utils';
import type { Tag } from '@/db/schema';
import { tagsDb } from '@/db/tags';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getTagRestrictions, isRequiredAttribute, isRequiredChild } from './tag-restrictions';

function isChildRequired(parentTagId: string, childTag: string): boolean {
    const restrictions = getTagRestrictions(parentTagId);
    if (!restrictions) return false;

    if (isRequiredChild(parentTagId, childTag)) {
        return true;
    }
    if (restrictions.nestedRequiredChildren) {
        for (const nestedChildren of Object.values(restrictions.nestedRequiredChildren)) {
            if (nestedChildren.includes(childTag.toLowerCase())) {
                return true;
            }
        }
    }
    return false;
}

export function TagInfo() {
    const { tagId } = useParams();
    const navigate = useNavigate();

    const tags = useLiveQuery<Tag[]>(() => tagsDb.tags.toArray(), []);

    useEffect(() => {
        if (tags && tags.length > 0 && !tagId) {
            navigate(`/search/tag/${encodeURIComponent(tags[0].id)}`, {
                replace: true,
            });
        }
    }, [tags, tagId, navigate]);

    const selected: Tag | null = useMemo(() => {
        if (!tags || tags.length === 0) return null;
        return tags.find(t => t.id === tagId) ?? tags[0];
    }, [tags, tagId]);

    const bookNames = useMemo(() => {
        if (!selected) return [] as string[];
        return Object.keys(selected.booksUsingTag || {}).sort((a, b) => a.localeCompare(b));
    }, [selected]);

    const htmlChildren = useMemo(() => {
        if (!selected) return [] as string[];
        const filtered = selected.children.filter(c => isValidHtmlTag(c));
        return filtered.sort((a, b) => {
            const aRequired = isChildRequired(selected.id, a);
            const bRequired = isChildRequired(selected.id, b);
            if (aRequired && !bRequired) return -1;
            if (!aRequired && bRequired) return 1;
            return a.localeCompare(b);
        });
    }, [selected]);

    const zyChildren = useMemo(() => {
        if (!selected) return [] as string[];
        const filtered = selected.children.filter(c => isZyTag(c));
        return filtered.sort((a, b) => {
            const aRequired = isChildRequired(selected.id, a);
            const bRequired = isChildRequired(selected.id, b);
            if (aRequired && !bRequired) return -1;
            if (!aRequired && bRequired) return 1;
            return a.localeCompare(b);
        });
    }, [selected]);

    const sortedAttributes = useMemo(() => {
        if (!selected) return [] as string[];
        return [...selected.attributes].sort((a, b) => {
            const aRequired = isRequiredAttribute(selected.id, a);
            const bRequired = isRequiredAttribute(selected.id, b);
            if (aRequired && !bRequired) return -1;
            if (!aRequired && bRequired) return 1;
            return a.localeCompare(b);
        });
    }, [selected]);

    if (!tags) return <div>Loading...</div>;
    if (!selected) return <div>No tags available</div>;

    return (
        <div className="flex flex-col max-h-[100vh] overflow-y-auto">
            <h1 className="text-[38px] font-bold">{`<${selected.id}>`}</h1>
            <div className="m-2 flex flex-col rounded-md border p-2 text-left">
                <div>Element used {selected.count} times</div>
                <div>
                    <h2 className="font-bold">Child Elements:</h2>
                    <div className="mt-2">
                        <h3 className="font-bold">HTML Tags:</h3>
                        <ul className="flex flex-wrap">
                            {htmlChildren.length > 0
                                ? htmlChildren.map(child => {
                                      const required = isChildRequired(selected.id, child);
                                      return (
                                          <li
                                              key={child}
                                              className={`m-2 w-fit rounded-md p-1 ${required ? 'bg-red-100' : 'bg-blue-100'}`}
                                          >
                                              <Link to={`/search/tag/${child}`}>
                                                  <span className={`font-bold ${required ? 'text-red-700' : ''}`}>
                                                      {required ? '*' : ''}
                                                      {`<${child}>`}
                                                  </span>
                                              </Link>
                                          </li>
                                      );
                                  })
                                : '(none)'}
                        </ul>
                    </div>
                    <div className="mt-2">
                        <h3 className="font-bold">ZyTags:</h3>
                        <ul className="flex flex-wrap">
                            {zyChildren.length > 0
                                ? zyChildren.map(child => {
                                      const required = isChildRequired(selected.id, child);
                                      return (
                                          <li
                                              key={child}
                                              className={`m-2 w-fit rounded-md p-1 ${required ? 'bg-red-100' : 'bg-orange-100'}`}
                                          >
                                              <Link to={`/search/tag/${child}`}>
                                                  <span className={`font-bold ${required ? 'text-red-700' : ''}`}>
                                                      {required ? '*' : ''}
                                                      {`<${child}>`}
                                                  </span>
                                              </Link>
                                          </li>
                                      );
                                  })
                                : '(none)'}
                        </ul>
                    </div>
                </div>
                <br />
                <div>
                    <h2 className="font-bold">Common Attributes:</h2>
                    <ul className="flex flex-wrap">
                        {sortedAttributes.length > 0
                            ? sortedAttributes.map(attr => {
                                  const required = isRequiredAttribute(selected.id, attr);
                                  return (
                                      <li
                                          key={attr}
                                          className={`m-2 w-fit rounded-md p-1 ${required ? 'bg-red-100' : 'bg-gray-100'}`}
                                      >
                                          <span className={required ? 'font-bold text-red-700' : ''}>
                                              {required ? '*' : ''}
                                              {attr}
                                          </span>
                                      </li>
                                  );
                              })
                            : '(none)'}
                    </ul>
                </div>
            </div>
            <div className="m-2 flex flex-col rounded-md border p-2 text-left">
                <h2 className="font-bold">Books Using This Tag:</h2>
                <ul className="flex flex-wrap">
                    {bookNames.length > 0
                        ? bookNames.map(name => (
                              <li
                                  key={name}
                                  className="m-2 w-fit rounded-md bg-green-100 p-1"
                              >
                                  <Link to={`/search/book/${encodeURIComponent(name)}`}>{name}</Link>
                              </li>
                          ))
                        : '(none)'}
                </ul>
            </div>
        </div>
    );
}
